import React, {useState} from "react"
import {PageProps} from "gatsby"
import {css} from "@emotion/react"
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome"
import Gallery from "react-photo-gallery"

import {NewsItem} from "../../types"

import Layout from "../components/layout"
import PageWidth from "../components/page-width"
import SingleBanner from "../components/single-banner"
import NewsLabel from "../components/news-label"
import {formatDate} from "../util/date"
import {Downloads} from "../components/downloads"

const NewsItemTemplate = (page: PageProps) => {
  const data = page.pageContext as NewsItem

  const imagesForGallery =
    data.imageGallery?.map(image => ({
      src: image.url,
      width: image.width || 300,
      height: image.height || 200,
    })) || []

  const [isOpen, setIsOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState<string | null>(null)

  const openModal = (imageUrl: string) => {
    setSelectedImage(imageUrl)
    setIsOpen(true)
  }

  const closeModal = () => {
    setIsOpen(false)
    setSelectedImage(null)
  }

  return (
    <Layout title={data.title} currentPage={data}>
      <SingleBanner
        bannerTitle="Nieuws"
        bannerImage={{
          url: data.headerImage
            ? data.headerImage.url
            : "https://www.datocms-assets.com/37731/1610466114-homepage-about-2.jpg",
        }}
        size="small"
      />
      <PageWidth text>
        <div css={containerStyles}>
          <h3>{data.title}</h3>
          <div className="label-container">
            <NewsLabel label="Nieuws" />
            {data.label && <NewsLabel label={data.label} />}
          </div>
          <p>
            <em>
              Geplaatst op:{" "}
              {formatDate(data.date)}
            </em>
          </p>
        </div>

        <p>
          <strong>{data.teaser}</strong>
        </p>
        <div dangerouslySetInnerHTML={{__html: data.content}} />

        {imagesForGallery.length > 0 && (
          <>
            <Gallery
              photos={imagesForGallery}
              columns={3}
              onClick={(event, {photo}) => openModal(photo.src)}
            />
            {isOpen && (
              <div css={modalOverlay} onClick={closeModal}>
                <div css={modalContent}>
                  <img src={selectedImage!} alt="Fullscreen image" />
                  <button css={modalClose} onClick={closeModal}>
                    <FontAwesomeIcon icon={["far", "xmark"]} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {data.attachments.length > 0 && (
          <div css={downloadStyle}>
            <Downloads externalDownloads={data.attachments.map(({url, title}) => ({destination: url, text: title || "Download"}))} />
          </div>
        )}
      </PageWidth>
    </Layout>
  )
}

export default NewsItemTemplate

const downloadStyle = css`
  margin-top: 7rem;
`

const containerStyles = css`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  h3 {
    margin-bottom: 0;
  }
  .label-container {
    display: flex;
    gap: 1rem;
  }
`

const modalOverlay = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.8);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`

const modalContent = css`
  position: relative;
  background: #fff;
  padding: 10px;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  max-width: 90vw;
  max-height: 90vh;
  overflow: hidden;

  img {
    margin: 3rem auto;
    max-width: 100%;
    max-height: 80vh;
    display: block;
    border-radius: 5px;
  }
`

const modalClose = css`
  position: absolute;
  top: 10px;
  right: 10px;
  background: none;
  border: none;
  font-size: 20px;
  color: black;
  cursor: pointer;
  transition: color 0.2s ease-in-out;
`
